import type { AdminGame, DxMode, UpdateGameRequest } from '@mcgames/contracts';
import { Button, Select, TextInput, Toggle } from '@mcgames/ui';
import { useId, useMemo, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorText } from './ErrorText.tsx';
import { useGameMutation } from './game-hooks.ts';
import styles from './Games.module.css';
import {
  DX_MODES,
  GAMESCOPE_NUMBER_FIELDS,
  duplicateEnvNames,
  linuxDraftFromGame,
  linuxSettingsFromDraft,
  newEnvRow,
  newGamescopeDraft,
  type EnvRow,
  type GamescopeDraft,
  type LinuxDraft,
  type TriState,
} from './linux-draft.ts';

const TRI_STATES: readonly TriState[] = ['unset', 'on', 'off'];

export type LinuxTabProps = {
  game: AdminGame;
  /** The unsaved edits, owned by the editor so they survive a switch to another tab; `null` when there are none. */
  draft: LinuxDraft | null;
  onDraftChange: (draft: LinuxDraft | null) => void;
};

export function LinuxTab({ game, draft, onDraftChange }: LinuxTabProps) {
  const { t } = useTranslation('admin');
  const hintId = useId();
  const save = useGameMutation<Required<Pick<UpdateGameRequest, 'linuxDefaults'>>>(
    game.id,
    'PATCH',
  );
  const saved = useMemo(() => linuxDraftFromGame(game), [game]);
  const current = draft ?? saved;
  const dirty =
    draft !== null &&
    JSON.stringify(linuxSettingsFromDraft(draft)) !== JSON.stringify(linuxSettingsFromDraft(saved));
  const duplicates = duplicateEnvNames(current.env);

  function update(changes: Partial<LinuxDraft>) {
    onDraftChange({ ...current, ...changes });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (duplicates.size > 0) return;
    save.mutate(
      { linuxDefaults: linuxSettingsFromDraft(current) },
      { onSuccess: () => onDraftChange(null) },
    );
  }

  const triOptions = TRI_STATES.map((state) => ({
    value: state,
    label: t(state === 'unset' ? 'games.linux.notSet' : `games.linux.${state}`),
  }));

  return (
    <form className={styles['form']} onSubmit={submit}>
      <p className={styles['muted']}>{t('games.linux.intro')}</p>
      <TextInput
        label={t('games.linux.protonVersion')}
        placeholder="GE-Proton10-25"
        spellCheck={false}
        value={current.protonVersion}
        aria-describedby={hintId}
        onChange={(event) => update({ protonVersion: event.target.value })}
      />
      <span id={hintId} className={styles['hint']}>
        {t('games.linux.protonVersionHint')}
      </span>
      <Select<DxMode | 'unset'>
        label={t('games.linux.dxMode')}
        value={current.dxMode}
        options={[
          { value: 'unset', label: t('games.linux.notSet') },
          ...DX_MODES.map((mode) => ({ value: mode, label: t(`games.linux.dx.${mode}`) })),
        ]}
        onChange={(dxMode) => update({ dxMode })}
      />
      <EnvEditor rows={current.env} duplicates={duplicates} onChange={(env) => update({ env })} />
      <div className={styles['row']}>
        <Select<TriState>
          label={t('games.linux.mangohud')}
          value={current.mangohud}
          options={triOptions}
          onChange={(mangohud) => update({ mangohud })}
        />
        <Select<TriState>
          label={t('games.linux.gamemode')}
          value={current.gamemode}
          options={triOptions}
          onChange={(gamemode) => update({ gamemode })}
        />
      </div>
      <Select<'unset' | 'configured'>
        label={t('games.linux.gamescope.label')}
        value={current.gamescope === null ? 'unset' : 'configured'}
        options={[
          { value: 'unset', label: t('games.linux.notSet') },
          { value: 'configured', label: t('games.linux.gamescope.configured') },
        ]}
        onChange={(choice) =>
          update({
            gamescope: choice === 'unset' ? null : (current.gamescope ?? newGamescopeDraft()),
          })
        }
      />
      {current.gamescope !== null && (
        <GamescopeFields
          value={current.gamescope}
          onChange={(gamescope) => update({ gamescope })}
        />
      )}
      <TextInput
        label={t('games.linux.launchArgs')}
        spellCheck={false}
        value={current.launchArgs}
        onChange={(event) => update({ launchArgs: event.target.value })}
      />
      <span className={styles['hint']}>{t('games.linux.launchArgsHint')}</span>
      <ErrorText error={save.error} />
      <div className={styles['row']}>
        <Button type="submit" disabled={save.isPending || duplicates.size > 0}>
          {t('games.linux.save')}
        </Button>
        <p role="status" className={dirty ? styles['hint'] : styles['status']}>
          {dirty
            ? t('games.linux.unsaved')
            : save.isSuccess && !save.isPending
              ? t('games.linux.saved')
              : ''}
        </p>
      </div>
    </form>
  );
}

type EnvEditorProps = {
  rows: EnvRow[];
  duplicates: Set<string>;
  onChange: (rows: EnvRow[]) => void;
};

function EnvEditor({ rows, duplicates, onChange }: EnvEditorProps) {
  const { t } = useTranslation('admin');

  function change(row: EnvRow, changes: Partial<EnvRow>) {
    onChange(rows.map((r) => (r === row ? { ...row, ...changes } : r)));
  }

  return (
    <div className={styles['fieldset']}>
      <h3 className={styles['subheading']}>{t('games.linux.env.heading')}</h3>
      {rows.length === 0 && <p className={styles['muted']}>{t('games.linux.env.none')}</p>}
      {rows.map((row, index) => {
        const position = index + 1;
        const duplicate = duplicates.has(row.name.trim());
        return (
          <div
            key={row.key}
            role="group"
            aria-label={t('games.linux.env.group', { position })}
            className={styles['envRow']}
          >
            <TextInput
              label={t('games.linux.env.name')}
              spellCheck={false}
              value={row.name}
              error={duplicate ? t('games.linux.env.duplicate') : undefined}
              onChange={(event) => change(row, { name: event.target.value })}
            />
            <TextInput
              label={t('games.linux.env.value')}
              spellCheck={false}
              value={row.value}
              onChange={(event) => change(row, { value: event.target.value })}
            />
            <Button
              size="sm"
              variant="danger"
              onClick={() => onChange(rows.filter((r) => r !== row))}
            >
              {t('games.linux.env.remove', { position })}
            </Button>
          </div>
        );
      })}
      <div className={styles['row']}>
        <Button variant="secondary" onClick={() => onChange([...rows, newEnvRow()])}>
          {t('games.linux.env.add')}
        </Button>
      </div>
      <span className={styles['hint']}>{t('games.linux.env.hint')}</span>
    </div>
  );
}

type GamescopeFieldsProps = {
  value: GamescopeDraft;
  onChange: (value: GamescopeDraft) => void;
};

function GamescopeFields({ value, onChange }: GamescopeFieldsProps) {
  const { t } = useTranslation('admin');
  return (
    <div className={styles['gamescope']}>
      <Toggle
        label={t('games.linux.gamescope.enabled')}
        checked={value.enabled}
        onChange={(enabled) => onChange({ ...value, enabled })}
      />
      <div className={styles['numberGrid']}>
        {GAMESCOPE_NUMBER_FIELDS.map((field) => (
          <TextInput
            key={field}
            label={t(`games.linux.gamescope.${field}`)}
            type="number"
            min={1}
            step={1}
            value={value[field]}
            onChange={(event) => onChange({ ...value, [field]: event.target.value })}
          />
        ))}
      </div>
      <span className={styles['hint']}>{t('games.linux.gamescope.hint')}</span>
      <Toggle
        label={t('games.linux.gamescope.fsr')}
        checked={value.fsr}
        onChange={(fsr) => onChange({ ...value, fsr })}
      />
      <Toggle
        label={t('games.linux.gamescope.fullscreen')}
        checked={value.fullscreen}
        onChange={(fullscreen) => onChange({ ...value, fullscreen })}
      />
    </div>
  );
}
